"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { Loader2, MoreHorizontal, Pencil, Plus, Trash2 } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@repo/ui";
import {
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@repo/ui";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@repo/ui";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@repo/ui";
import { Input } from "@repo/ui";
import { Label } from "@repo/ui";
import { Button } from "@repo/ui";
import { toast } from "sonner";
import { RenderIcon } from "../render-icon";
import {
  useDashboards,
  useCreateDashboard,
  useRenameDashboard,
  useDeleteDashboard,
} from "@/hooks/dashboards/use-dashboards";
import { useDashboardsSubscription } from "@/hooks/dashboards/use-dashboards-subscription";
import type { Dashboard } from "@/types/dashboards";
import type { NavItem } from "./app-sidebar";

/**
 * "Dashboards" rendered as a collapsible folder: clicking the row toggles the
 * list of dashboards (one sub-item each); the trailing `+` (in place of the
 * usual chevron) opens the create dialog. Replaces the standalone dashboard
 * list page.
 */
export const DashboardsNavFolder = ({ item }: { item: NavItem }) => {
  const router = useRouter();
  const pathname = usePathname();
  const { setOpenMobile } = useSidebar();

  const { data: dashboards = [] } = useDashboards();
  const createDashboard = useCreateDashboard();
  const renameDashboard = useRenameDashboard();
  const deleteDashboard = useDeleteDashboard();

  // Keep the dashboard list live (create/update/delete) via the shared websocket.
  // Mounted here because this folder is the single component that renders only
  // when the Dashboards feature is active.
  useDashboardsSubscription();

  // Open by default when the user is already viewing a dashboard.
  const [isOpen, setIsOpen] = useState(pathname.startsWith(item.url));
  const itemRef = useRef<HTMLLIElement>(null);
  // Only a click should scroll; the default-open on a dashboard page should not.
  const scrollOnOpen = useRef(false);

  useEffect(() => {
    if (!isOpen || !scrollOnOpen.current) return;
    scrollOnOpen.current = false;
    itemRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [isOpen]);

  const [createOpen, setCreateOpen] = useState(false);
  const creating = createDashboard.isPending;
  const [name, setName] = useState("");

  const [renameTarget, setRenameTarget] = useState<Dashboard | null>(null);
  const [renameName, setRenameName] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<Dashboard | null>(null);

  const handleCreate = async () => {
    if (!name.trim() || creating) return;
    let dashboard;
    try {
      dashboard = await createDashboard.mutateAsync({ name: name.trim() });
    } catch (error) {
      console.error("error creating dashboard", error);
      return;
    }
    setCreateOpen(false);
    setName("");
    setIsOpen(true);
    setOpenMobile(false);
    router.push(`/p/dashboard/${dashboard.id}`);
  };

  const handleRename = async () => {
    if (!renameTarget || !renameName.trim() || renameDashboard.isPending) return;
    if (renameName.trim() === renameTarget.name) {
      setRenameTarget(null);
      return;
    }
    try {
      await renameDashboard.mutateAsync({ id: renameTarget.id, name: renameName.trim() });
      setRenameTarget(null);
    } catch (error) {
      toast.error("Failed to rename dashboard");
      console.error("error renaming dashboard", error);
    }
  };

  const handleDelete = async () => {
    const target = deleteTarget;
    if (!target) return;
    try {
      await deleteDashboard.mutateAsync(target.id);
      if (pathname === `/p/dashboard/${target.id}`) {
        router.push("/");
      }
      setDeleteTarget(null);
    } catch (error) {
      toast.error("Failed to delete dashboard");
      console.error("error deleting dashboard", error);
    }
  };

  return (
    <>
      <Collapsible
        asChild
        open={isOpen}
        onOpenChange={(open) => {
          scrollOnOpen.current = open;
          setIsOpen(open);
        }}
      >
        <SidebarMenuItem ref={itemRef}>
          <SidebarMenuButton asChild tooltip={item.title}>
            <CollapsibleTrigger className="group flex items-center gap-2 text-muted-foreground">
              <RenderIcon iconName={item.icon ?? ""} size={14} className="text-muted-foreground" />
              <div className="w-full overflow-hidden text-left">{item.title}</div>
            </CollapsibleTrigger>
          </SidebarMenuButton>

          {/* `+` replaces the expand chevron — creates a new dashboard. */}
          <SidebarMenuAction
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setCreateOpen(true);
            }}
            title="New dashboard"
            // Keep the action's default `top-1.5` so the `+` stays anchored to the
            // parent "Dashboards" row — `top-1/2` would center it over the whole
            // expanded item (parent + children) and land it on the first child.
            className="text-muted-foreground -mt-[2px]"
          >
            <Plus />
            <span className="sr-only">New dashboard</span>
          </SidebarMenuAction>

          <CollapsibleContent>
            <SidebarMenuSub className="pl-2 pr-0">
              {dashboards.length === 0 ? (
                <div className="px-2 py-1 text-xs text-muted-foreground">No dashboards yet</div>
              ) : (
                dashboards.map((d) => {
                  const url = `/p/dashboard/${d.id}`;
                  return (
                    <SidebarMenuSubItem key={d.id} className="group/dash relative">
                      <SidebarMenuSubButton asChild isActive={pathname === url}>
                        <Link
                          href={url}
                          onClick={() => setOpenMobile(false)}
                          className="truncate pr-6"
                        >
                          {d.name}
                        </Link>
                      </SidebarMenuSubButton>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <SidebarMenuAction
                            // Center on the h-7 sub-row: the default `top-1.5` is tuned for
                            // the taller menu button, so override with a translate-based center.
                            className="top-1/2 -translate-y-1/2 text-muted-foreground opacity-100 transition-opacity data-[state=open]:opacity-100 group-hover/dash:opacity-100 md:opacity-0"
                            title="Dashboard options"
                          >
                            <MoreHorizontal />
                            <span className="sr-only">Dashboard options</span>
                          </SidebarMenuAction>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent side="right" align="start" className="w-40">
                          <DropdownMenuItem
                            onSelect={() => {
                              setRenameName(d.name);
                              setRenameTarget(d);
                            }}
                          >
                            <Pencil className="mr-2 h-3.5 w-3.5" />
                            Rename
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onSelect={() => setDeleteTarget(d)}
                          >
                            <Trash2 className="mr-2 h-3.5 w-3.5" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </SidebarMenuSubItem>
                  );
                })
              )}
            </SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New dashboard</DialogTitle>
            <DialogDescription>
              Dashboards are saved to your Pylo workspace and shared with your organization.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="sidebar-dashboard-name">Name</Label>
              <Input
                id="sidebar-dashboard-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleCreate();
                }}
                placeholder="e.g. Product analytics"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setCreateOpen(false)}
              disabled={creating}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleCreate} disabled={!name.trim() || creating}>
              {creating ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
              {creating ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={renameTarget !== null}
        onOpenChange={(o) => {
          if (!o) setRenameTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename dashboard</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="rename-dashboard-name">Name</Label>
            <Input
              id="rename-dashboard-name"
              value={renameName}
              onChange={(e) => setRenameName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleRename();
              }}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setRenameTarget(null)}
              disabled={renameDashboard.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleRename}
              disabled={!renameName.trim() || renameDashboard.isPending}
            >
              {renameDashboard.isPending ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(o) => {
          if (!o) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleteTarget?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes the dashboard and all its widgets. This can’t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteDashboard.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
